import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { AlertTriangle, Clock, X } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return iso; }
}

function urgencyLabel(sev, dueIso) {
  if (sev === 5) return "overdue";
  if (sev === 4) return "due today";
  if (sev === 3) return "due tomorrow";
  if (sev === 2) return "due in 2 days";
  if (sev === 1) return "due in 3 days";
  return `due ${fmtDate(dueIso)}`;
}

/**
 * Shows payment reminders based on the client's manually-entered payments[].
 * - Severity 1-2 (3 days / 2 days): top banner (no ack required, soft dismissal).
 * - Severity 3-5 (1 day / due today / overdue): full-screen modal popup requiring
 *   the viewer to click "I acknowledge" before it will go away. The ack is stored
 *   server-side per user per severity level — if the urgency escalates, the popup
 *   re-surfaces.
 * - Amounts are deliberately never shown.
 */
export default function PaymentDueReminders({ clientId }) {
  const [reminders, setReminders] = useState([]);
  const [acking, setAcking] = useState({}); // { [payment_id]: true }

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await api.get(`/clients/${clientId}/payment-reminders`);
      setReminders(r.data || []);
    } catch {
      // silent — reminders are advisory
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const ack = async (r) => {
    setAcking((s) => ({ ...s, [r.payment_id]: true }));
    try {
      await api.post(`/clients/${clientId}/payments/${r.payment_id}/ack`);
      setReminders((prev) => prev.filter((x) => x.payment_id !== r.payment_id));
      toast.success("Acknowledged");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record acknowledgement");
    } finally {
      setAcking((s) => { const n = { ...s }; delete n[r.payment_id]; return n; });
    }
  };

  if (!reminders.length) return null;

  const popup = reminders.find((r) => r.level === "popup");
  const banners = reminders.filter((r) => r.level === "banner");

  return (
    <>
      {banners.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="payment-reminders-banners">
          {banners.map((r) => (
            <div key={r.payment_id}
              data-testid={`payment-banner-${r.payment_id}`}
              className="tt-card p-3 flex items-center justify-between gap-3 border-amber-500/40 bg-amber-500/5">
              <div className="flex items-center gap-2 text-[12px] min-w-0">
                <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="text-zinc-200 truncate">
                  <span className="font-medium">{r.note}</span>
                  <span className="text-zinc-500"> · {urgencyLabel(r.severity, r.due_date)} · {fmtDate(r.due_date)}</span>
                </span>
              </div>
              <Button size="sm" variant="ghost" disabled={!!acking[r.payment_id]}
                onClick={() => ack(r)}
                data-testid={`payment-banner-ack-${r.payment_id}`}
                className="h-7 text-[11px] shrink-0">
                I acknowledge
              </Button>
            </div>
          ))}
        </div>
      )}
      {popup && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="payment-popup">
          <div className="tt-card p-6 max-w-md w-full border-amber-500/50 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className={`w-4 h-4 ${popup.severity === 5 ? "text-[#EF4444]" : "text-amber-400"}`} />
              <div className="t-eyebrow">{popup.severity === 5 ? "Payment overdue" : "Payment reminder"}</div>
            </div>
            <h2 className="font-display text-[22px] text-zinc-100 mb-2" data-testid="payment-popup-note">{popup.note}</h2>
            <div className="text-[13px] text-zinc-400 mb-5">
              {popup.severity === 5 ? (
                <>This payment was due on <span className="text-zinc-100 font-mono">{fmtDate(popup.due_date)}</span> and is now overdue. Please acknowledge to continue.</>
              ) : popup.severity === 4 ? (
                <>This payment is <span className="text-zinc-100">due today</span> (<span className="font-mono">{fmtDate(popup.due_date)}</span>). Please acknowledge to continue.</>
              ) : (
                <>This payment is <span className="text-zinc-100">due tomorrow</span> (<span className="font-mono">{fmtDate(popup.due_date)}</span>). Please acknowledge to continue.</>
              )}
            </div>
            <div className="flex justify-end">
              <Button size="sm" disabled={!!acking[popup.payment_id]}
                onClick={() => ack(popup)}
                data-testid="payment-popup-ack"
                className="bg-white text-black hover:bg-zinc-200 h-9 text-[12px] px-4">
                I acknowledge
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
